import JSZip from 'jszip';
import * as Yup from 'yup';
import {useIntl} from 'react-intl';
import {Button, IconButton} from '@material-ui/core';
import {GetApp as GetAppIcon} from '@material-ui/icons';
import {Field, FieldProps, Form, Formik, FormikProps} from 'formik';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {downloadBlob} from '../../../utils';
import {ScriptUploadField} from '../Fields/ScriptUploadField';
import {StudioSelect} from '../../../base-components/StudioSelect';
import {StudioPopover} from '../../../base-components/StudioPopover';
import {StudioTooltip} from '../../../base-components/StudioTooltip';
import Waiting from '../../../base-components/StudioWaiting/Waiting';
import {StudioTextField} from '../../../base-components/StudioTextField';
import {PRE_AND_POST_PROCESSORS_SETTINGS} from '../../../config/constants';
import {ExecutionContainer} from '../../../types/model/framework/ExecutionContainer';
import {getPostProcessorFile} from '../../../api/postprocessor/ViewPostProcessorFile';
import {PostProcessorResponse} from '../../../types/postprocessor/PostProcessorResponse';
import {UpdatePostProcessorRequest} from '../../../types/postprocessor/UpdatePostProcessorRequest';
import {
  ParameterFieldArray,
  parameterArrayValidation,
} from '../Fields/ParameterFieldArray';

import '../index.scss';

const defaultTemplate: PostProcessorResponse = {
  id: '',
  name: '',
  version: 0,
  projectId: '',
  filePaths: [],
  parameters: [],
  description: '',
  studioVersion: 0,
  applicationId: '',
  outOfPackage: false,
  applicationTypeOptions: {},
  postProcessorTypeOptions: {},
  executionContainerOptions: {},
};

const validationSchema = Yup.object({
  id: Yup.string().required('Required'),
  name: Yup.string().required('Required'),
  projectId: Yup.string().required('Required'),
  applicationType: Yup.string().required('Required'),
  postProcessorType: Yup.string().required('Required'),
  executionContainer: Yup.string().required('Required'),
  filePaths: Yup.object()
    .test('file-exists', 'Required', files => Boolean(files && Object.keys(files).length))
    .test('file-not-empty', 'File is empty', files =>
      Boolean(!files || Object.values(files)?.[0])
    ),
  parameters: parameterArrayValidation,
});

type PostProcessingFormProps = {
  title: string;
  projectId: string;
  returnText?: string;
  viewOnly?: boolean;
  template?: PostProcessorResponse;
  filePaths?: Record<string, string>;
  isInitializingData?: boolean;
  onCancel: () => void;
  onSubmit: (values: UpdatePostProcessorRequest) => Promise<void>;
};

const DEFAULT_FILE_PATHS = {};

export const PostProcessingForm = ({
  title,
  viewOnly,
  template,
  projectId,
  returnText,
  isInitializingData = false,
  filePaths: backendFilePaths = DEFAULT_FILE_PATHS,
  onSubmit,
  onCancel,
}: PostProcessingFormProps) => {
  const intl = useIntl();
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const formikRef = useRef<FormikProps<UpdatePostProcessorRequest>>(null);

  const [newScripts, setNewScripts] = useState<File[]>([]);
  const [backendScripts, setBackendScripts] = useState<File[]>([]);
  const [filePaths, setFilePaths] = useState<Record<string, string>>({});
  const scripts = useMemo(
    () =>
      [...newScripts, ...backendScripts].sort((fileA, fileB) =>
        fileA.name.localeCompare(fileB.name)
      ),
    [newScripts, backendScripts]
  );

  useEffect(() => {
    (async function updateFileContents() {
      const newFileContents: Record<string, string> = {};

      for (const file of scripts) {
        try {
          newFileContents[file.name] = await file.text();
        } catch (error) {
          console.error(`Error reading file ${file.name}:`, error);
        }
      }

      setFilePaths(newFileContents);
      formikRef.current?.setFieldValue('filePaths', newFileContents);
    })();
  }, [scripts]);

  const [, setShouldResetForm] = useState(false);
  useEffect(
    function resetFormWhenFileContentsChange() {
      setShouldResetForm(shouldResetForm => {
        let result = shouldResetForm;

        if (shouldResetForm && formikRef.current) {
          result = false;

          formikRef.current.resetForm({
            values: {
              ...formikRef.current?.values,
              filePaths,
            },
          });
        }

        return result;
      });
    },
    [filePaths]
  );

  const {
    id,
    name,
    parameters,
    description,
    useDefaultFile,
    modelPurpose,
    applicationType,
    postProcessorType,
    executionContainer,
    applicationTypeOptions,
    executionContainerOptions,
    postProcessorTypeOptions,
  } = useMemo(() => template || defaultTemplate, [template]);

  const initialValues: UpdatePostProcessorRequest = useMemo(
    () => ({
      id,
      name,
      projectId,
      filePaths,
      parameters,
      description,
      applicationType,
      postProcessorType,
      executionContainer,
    }),
    [
      id,
      name,
      projectId,
      filePaths,
      parameters,
      description,
      applicationType,
      postProcessorType,
      executionContainer,
    ]
  );

  const downloadAllFiles = useCallback(async () => {
    const archive = new JSZip();
    const scripts = [...newScripts, ...backendScripts];

    for (let file of scripts) {
      const fileContent = file.slice(0, file.size, file.type);

      await archive.file(file.name, fileContent);
    }

    const zipBlob = await archive.generateAsync({type: 'blob'});
    const fileName = 'post-processor.zip';
    const url = URL.createObjectURL(
      new File([zipBlob], fileName, {type: 'application/zip'})
    );

    downloadBlob({
      blobUrl: url,
      fileName: 'post-processor.zip',
    });
  }, [newScripts, backendScripts]);

  const resetOnExecutionContainerChange = (
    event: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>,
    values: UpdatePostProcessorRequest
  ) => {
    const newExecutionContainer = event.target.value as ExecutionContainer;

    if (
      values.executionContainer &&
      values.executionContainer !== newExecutionContainer
    ) {
      setNewScripts([]);
      setBackendScripts([]);
      formikRef.current?.setFieldValue('filePaths', {});
    }
  };

  const handleFileUpload = useCallback(
    async (files: File[]) => {
      const currentNewFiles: File[] = [];
      const filteredBackendScripts: File[] = [];

      for (const file of files) {
        const isBackend = backendFilePaths[file.name] !== undefined;

        isBackend ? filteredBackendScripts.push(file) : currentNewFiles.push(file);
      }

      setNewScripts(currentNewFiles);
      setBackendScripts(filteredBackendScripts);
      setShouldResetForm(true);
    },
    [backendFilePaths]
  );

  useEffect(
    function loadingBackendFiles() {
      if (id && projectId && postProcessorType && executionContainer) {
        setIsLoadingFiles(true);

        (async function getFiles() {
          const backendFiles: File[] = [];

          for (const [key] of Object.entries(backendFilePaths)) {
            const content = await getPostProcessorFile({
              id,
              projectId,
              fileName: key,
              postProcessorType,
              executionContainer,
            });

            backendFiles.push(new File([content.data], key));
          }

          setBackendScripts(backendFiles);
          setShouldResetForm(true);
          setIsLoadingFiles(false);
        })();
      }
    },
    [backendFilePaths, id, projectId, postProcessorType, executionContainer]
  );

  return isLoadingFiles || isInitializingData ? (
    <Waiting
      diameter={100}
      className="processing-waiter-container"
      loadingText={intl.formatMessage({
        id: 'project.postprocessor.loadingInitialFiles',
      })}
    />
  ) : (
    <Formik
      validateOnChange
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({values, isSubmitting}) => (
        <div className="processing-form">
          <header>
            <Button
              className="processing-form__back"
              variant="contained"
              disableElevation
              onClick={onCancel}
            >
              {returnText ??
                intl.formatMessage({
                  id: 'form.returnToDesign',
                })}
            </Button>
            <div>
              <StudioTooltip
                title={intl.formatMessage({
                  id: 'project.postprocessor.downloadFile',
                })}
              >
                <IconButton onClick={downloadAllFiles}>
                  <GetAppIcon />
                </IconButton>
              </StudioTooltip>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                form="post-processor-form"
                disabled={isSubmitting || viewOnly}
                disableElevation
              >
                {intl.formatMessage({
                  id: isSubmitting ? 'form.submitting' : 'form.submit',
                })}
              </Button>
            </div>
          </header>
          <div className="processing-form__main">
            <h2>{title}</h2>
            <Form id="post-processor-form" noValidate>
              <div className="processing-form__form-top">
                <Field name="name">
                  {({field, meta}: FieldProps<UpdatePostProcessorRequest['name']>) => (
                    <div>
                      <StudioTextField
                        className="processing-form__field"
                        label={
                          <React.Fragment>
                            {intl.formatMessage({id: 'sharedProcessor.form.name'})}
                            <StudioPopover
                              infoMessage={intl.formatMessage({
                                id: 'editPostProcessor.name.tooltip',
                              })}
                            />
                          </React.Fragment>
                        }
                        placeholder={intl.formatMessage({
                          id: 'sharedProcessor.form.name.placeholder',
                        })}
                        InputProps={field}
                        error={Boolean(meta.error && meta.touched)}
                        helperText={meta.error && meta.touched ? meta.error : ''}
                        disabled={viewOnly}
                        inputProps={{
                          maxLength: PRE_AND_POST_PROCESSORS_SETTINGS.name.maxLength,
                        }}
                      />
                    </div>
                  )}
                </Field>
                <Field name="description">
                  {({field}: FieldProps<UpdatePostProcessorRequest['description']>) => (
                    <StudioTextField
                      className="processing-form__field"
                      label={
                        <React.Fragment>
                          {intl.formatMessage({id: 'sharedProcessor.form.description'})}
                          <StudioPopover
                            infoMessage={intl.formatMessage({
                              id: 'editPostProcessor.description.tooltip',
                            })}
                          />
                        </React.Fragment>
                      }
                      placeholder={intl.formatMessage({
                        id: 'sharedProcessor.form.description.placeholder',
                      })}
                      InputProps={field}
                      disabled={viewOnly}
                      multiline={PRE_AND_POST_PROCESSORS_SETTINGS.description.multiline}
                      inputProps={{
                        className: PRE_AND_POST_PROCESSORS_SETTINGS.description.className,
                        maxLength: PRE_AND_POST_PROCESSORS_SETTINGS.description.maxLength,
                      }}
                    />
                  )}
                </Field>
                <Field name="applicationType">
                  {({
                    field,
                    meta,
                  }: FieldProps<UpdatePostProcessorRequest['applicationType']>) => (
                    <StudioSelect
                      className="processing-form__field"
                      label={intl.formatMessage({
                        id: 'sharedProcessor.form.appType',
                      })}
                      additionalLabel={
                        <StudioPopover
                          infoMessage={intl.formatMessage({
                            id: 'editPostProcessor.applicationType.tooltip',
                          })}
                        />
                      }
                      placeholder={intl.formatMessage({
                        id: 'sharedProcessor.form.appType.placeholder',
                      })}
                      options={applicationTypeOptions}
                      disablePlaceholder
                      SelectProps={{...field, value: field.value || ''}}
                      error={Boolean(meta.error && meta.touched)}
                      helperText={meta.error && meta.touched ? meta.error : ''}
                      disabled={viewOnly}
                    />
                  )}
                </Field>
                <Field name="postProcessorType">
                  {({
                    field,
                    meta,
                  }: FieldProps<UpdatePostProcessorRequest['postProcessorType']>) => (
                    <StudioSelect
                      className="processing-form__field"
                      label={intl.formatMessage({
                        id: 'sharedProcessor.form.ppType',
                      })}
                      additionalLabel={
                        <StudioPopover
                          infoMessage={intl.formatMessage({
                            id: 'editPostProcessor.postProcessorType.tooltip',
                          })}
                        />
                      }
                      placeholder={intl.formatMessage({
                        id: 'sharedProcessor.form.ppType.placeholder',
                      })}
                      options={postProcessorTypeOptions}
                      disablePlaceholder
                      SelectProps={{...field, value: field.value || ''}}
                      error={Boolean(meta.error && meta.touched)}
                      helperText={meta.error && meta.touched ? meta.error : ''}
                      disabled={viewOnly}
                    />
                  )}
                </Field>
                <Field name="executionContainer">
                  {({
                    field,
                    form,
                    meta,
                  }: FieldProps<UpdatePostProcessorRequest['executionContainer']>) => (
                    <StudioSelect
                      className="processing-form__field"
                      label={intl.formatMessage({
                        id: 'sharedProcessor.form.target',
                      })}
                      additionalLabel={
                        <StudioPopover
                          infoMessage={intl.formatMessage({
                            id: 'editPostProcessor.executionContainer.tooltip',
                          })}
                        />
                      }
                      placeholder={intl.formatMessage({
                        id: 'sharedProcessor.form.target.placeholder',
                      })}
                      options={executionContainerOptions}
                      SelectProps={{
                        ...field,
                        value: field.value || '',
                        onChange: e => {
                          resetOnExecutionContainerChange(e, values);
                          field.onChange(e);
                        },
                      }}
                      disablePlaceholder
                      error={Boolean(meta.error && meta.touched)}
                      helperText={meta.error && meta.touched ? meta.error : ''}
                      disabled={viewOnly}
                    />
                  )}
                </Field>
                <Field name="filePaths">
                  {({
                    form,
                    meta,
                  }: FieldProps<UpdatePostProcessorRequest['filePaths']>) => {
                    const {
                      applicationType,
                      postProcessorType,
                      executionContainer,
                    } = values;

                    return executionContainer ? (
                      <ScriptUploadField
                        disabled={viewOnly}
                        viewOnly={viewOnly}
                        filePaths={scripts}
                        useDefault={useDefaultFile}
                        modelPurpose={modelPurpose}
                        onChange={handleFileUpload}
                        className="processing-form__field"
                        errorText={meta.error && meta.touched ? meta.error : ''}
                        label={intl.formatMessage({id: 'sharedProcessor.form.script'})}
                        tooltip={intl.formatMessage({
                          id: 'editPostProcessor.filePaths.tooltip',
                        })}
                        allowDefault={
                          executionContainer === 'BROWSER' &&
                          postProcessorType === 'APPLICATION' &&
                          applicationType === 'OPEN_VX_APP'
                        }
                      />
                    ) : null;
                  }}
                </Field>
              </div>
              <hr />
              <h5>{intl.formatMessage({id: 'postProcessor.params.header'})}</h5>
              <ParameterFieldArray
                name="parameters"
                parameters={values.parameters || []}
                disabled={viewOnly}
              />
            </Form>
          </div>
        </div>
      )}
    </Formik>
  );
};
